import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface TimeSlotStats {
  timeSlot: string;
  successRate: number;
  totalVisits: number;
  successfulVisits: number;
  failedVisits: number;
}

const TIME_SLOT_RANGES = {
  'monday-afternoon': { day: 1, start: 15.5, end: 19.5, label: 'Montag 15:30-19:30' },
  'tuesday-morning': { day: 2, start: 10, end: 12.5, label: 'Dienstag 10:00-12:30' },
  'tuesday-afternoon': { day: 2, start: 15.5, end: 19.5, label: 'Dienstag 15:30-19:30' },
  'wednesday-morning': { day: 3, start: 10, end: 12.5, label: 'Mittwoch 10:00-12:30' },
  'wednesday-afternoon': { day: 3, start: 15.5, end: 19.5, label: 'Mittwoch 15:30-19:30' },
  'thursday-morning': { day: 4, start: 10, end: 12.5, label: 'Donnerstag 10:00-12:30' },
  'thursday-afternoon': { day: 4, start: 15.5, end: 19.5, label: 'Donnerstag 15:30-19:30' },
  'friday-morning': { day: 5, start: 10, end: 12.5, label: 'Freitag 10:00-12:30' },
  'friday-afternoon': { day: 5, start: 15.5, end: 19.5, label: 'Freitag 15:30-19:30' },
  'saturday-morning': { day: 6, start: 10, end: 14, label: 'Samstag 10:00-14:00' },
  'saturday-afternoon': { day: 6, start: 15.5, end: 17.5, label: 'Samstag 15:30-17:30' },
};

function getTimeSlotForVisit(timestamp: string): string | null {
  const date = new Date(timestamp);
  const dayOfWeek = date.getDay();
  const hours = date.getHours() + date.getMinutes() / 60;

  for (const [slotKey, slot] of Object.entries(TIME_SLOT_RANGES)) {
    if (slot.day === dayOfWeek && hours >= slot.start && hours < slot.end) {
      return slotKey;
    }
  }
  return null;
}

function isSuccessfulVisit(tags: string[]): boolean {
  return tags.some(tag => ['WIN', 'HOT', 'KT'].includes(tag));
}

function isFailedVisit(tags: string[]): boolean {
  return tags.some(tag => ['NA', 'NO', 'KI', 'BK'].includes(tag));
}

export function useD2DSuccessStatistics(userId: string) {
  return useQuery({
    queryKey: ['d2d-success-statistics', userId],
    queryFn: async () => {
      const { data: units, error } = await supabase
        .from('d2d_units')
        .select('id, tags, last_contact_at')
        .eq('user_id', userId)
        .not('last_contact_at', 'is', null);

      if (error) throw error;

      // Initialize statistics object
      const statsMap: Record<string, TimeSlotStats> = {};
      Object.entries(TIME_SLOT_RANGES).forEach(([key, slot]) => {
        statsMap[key] = {
          timeSlot: slot.label,
          successRate: 0,
          totalVisits: 0,
          successfulVisits: 0,
          failedVisits: 0,
        };
      });

      // Process each unit's contact history
      units?.forEach(unit => {
        if (!unit.last_contact_at || !unit.tags || unit.tags.length === 0) return;

        const timeSlot = getTimeSlotForVisit(unit.last_contact_at);
        if (!timeSlot || !statsMap[timeSlot]) return;

        const stats = statsMap[timeSlot];
        
        if (isSuccessfulVisit(unit.tags)) {
          stats.successfulVisits++;
          stats.totalVisits++;
        } else if (isFailedVisit(unit.tags)) {
          stats.failedVisits++;
          stats.totalVisits++;
        }
      });

      // Calculate success rates
      const statsArray = Object.values(statsMap).map(stats => {
        if (stats.totalVisits > 0) {
          stats.successRate = Math.round((stats.successfulVisits / stats.totalVisits) * 100);
        }
        return stats;
      });

      // Sort by success rate descending
      statsArray.sort((a, b) => b.successRate - a.successRate);

      return {
        statistics: statsArray,
        bestTimeSlot: statsArray.find(s => s.totalVisits > 0) || null,
        worstTimeSlot: statsArray.filter(s => s.totalVisits > 0).pop() || null,
        totalVisitsAnalyzed: statsArray.reduce((sum, s) => sum + s.totalVisits, 0),
      };
    },
    enabled: !!userId,
  });
}
